import { useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, ArrowRight, ChevronRight, CornerDownLeft, Search } from 'lucide-react'
import { splitPath } from '@/features/admin/hoje/hoje-logic'
import { adminMeQuery, adminRelatedQuery, adminSearchAllQuery } from '@/features/admin/shared/api'
import type { Related, RelatedRef } from '@/features/admin/shared/search-contract'
import { CONFIG_SECTIONS } from '@/features/erp/config/registry'
import { useFrenteNav } from '@/features/erp/frente-state'
import { FRENTES } from '@/features/erp/frentes'
import { cn } from '@/lib/utils'
import {
    buildSections, configPages, flatOptions, isMacPlatform, isPaletteShortcut, OPEN_EVENT, optionPath, relatedKey, relatedOf, shouldSearch, staffPages, wrapIndex,
    type PaletteOption,
} from './command-palette-logic'

/* COG-009 — ⌘K / Ctrl+K command palette, mounted once in AppShell. Opens from the shortcut anywhere or from the
   Sidebar trigger (window event OPEN_EVENT). Busca em tudo o que a pessoa pode abrir nesta frente: POST /admin/search
   (o texto vai no corpo) devolve grupos — Padrinhos, Crianças, Ocorrências, Empresas, Igrejas, Eventos, Embaixadores,
   OSPs, Equipe, Mensagens… — com até 5 itens e "ver mais"; "Telas" = o menu da frente (+ Configurações para o admin).
   Em telas largas (≥ 900 px) o painel da direita mostra o que está ligado ao item em destaque (POST
   /admin/search/related, com espera curta e cache do TanStack Query); em telas estreitas abre com → (ou o botão
   "Relacionados") e volta com ←. ↑/↓ movem, Enter abre, Esc fecha; o foco volta para quem abriu. */

const WIDE_QUERY = '(min-width: 900px)'
const SEARCH_DEBOUNCE_MS = 250
const RELATED_DEBOUNCE_MS = 180

/** Opens the palette (used by the Sidebar trigger). */
export function openCommandPalette() {
    window.dispatchEvent(new Event(OPEN_EVENT))
}

export function paletteShortcutLabel(): string {
    const platform = typeof navigator === 'undefined' ? '' : (navigator.platform || navigator.userAgent)
    return isMacPlatform(platform) ? '⌘K' : 'Ctrl K'
}

function useDebounced<T>(value: T, ms: number): T {
    const [v, setV] = useState(value)
    useEffect(() => {
        const t = window.setTimeout(() => setV(value), ms)
        return () => window.clearTimeout(t)
    }, [value, ms])
    return v
}

function useWide(): boolean {
    const subscribe = (cb: () => void) => {
        const mq = window.matchMedia?.(WIDE_QUERY)
        mq?.addEventListener('change', cb)
        return () => mq?.removeEventListener('change', cb)
    }
    return useSyncExternalStore(subscribe, () => !!window.matchMedia?.(WIDE_QUERY).matches, () => false)
}

export function CommandPalette() {
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (!isPaletteShortcut(e)) return
            e.preventDefault()
            setOpen(v => !v)
        }
        const onOpen = () => setOpen(true)
        document.addEventListener('keydown', onKey)
        window.addEventListener(OPEN_EVENT, onOpen)
        return () => {
            document.removeEventListener('keydown', onKey)
            window.removeEventListener(OPEN_EVENT, onOpen)
        }
    }, [])

    const close = useCallback(() => setOpen(false), [])
    if (!open) return null
    return <PaletteDialog onClose={close} />
}

const sectionLabel = 'px-3 pt-3 pb-1 text-[12px] text-muted'
const TONE_COLOR = { default: undefined, warn: 'var(--yellow-ink)', err: 'var(--red-ink)' } as const

function PaletteDialog({ onClose }: { onClose: () => void }) {
    const navigate = useNavigate()
    const wide = useWide()
    const [query, setQuery] = useState('')
    const [active, setActive] = useState(0)
    // Telas estreitas: a prévia toma o lugar da lista (→ abre, ← volta)
    const [showRelated, setShowRelated] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const opener = useRef<Element | null>(null)
    const baseId = useId()
    const optionId = (id: string) => `${baseId}-${id}`

    const adminMe = useQuery(adminMeQuery)
    const { role, frente, nav } = useFrenteNav()
    const debounced = useDebounced(query.trim(), SEARCH_DEBOUNCE_MS)
    const searchable = shouldSearch(debounced) && !!role
    const search = useQuery({ ...adminSearchAllQuery(debounced, role ? frente : null), enabled: searchable && !adminMe.isError })

    // Telas da frente; as Configurações só para o perfil admin
    const pages = useMemo(() => (role ? [...staffPages(nav, FRENTES[frente].label, role), ...(role === 'admin' ? configPages(CONFIG_SECTIONS) : [])] : []), [role, frente, nav])
    const groups = searchable && search.data?.q === debounced ? search.data.groups : undefined
    const sections = useMemo(() => buildSections(pages, query, shouldSearch(query) ? groups : undefined), [pages, query, groups])
    const options = useMemo(() => flatOptions(sections), [sections])
    const current = options[active] ?? null

    // Prévia: o item em destaque, com espera curta (↑/↓ rápidos não disparam um pedido por linha)
    const ref = relatedOf(current)
    const debouncedRefKey = useDebounced(relatedKey(ref), RELATED_DEBOUNCE_MS)
    const settledRef: RelatedRef | null = ref && relatedKey(ref) === debouncedRefKey ? ref : null
    const previewOn = wide || showRelated
    const related = useQuery({ ...adminRelatedQuery(settledRef, role ? frente : null), enabled: previewOn && settledRef !== null })

    // Focus in, scroll lock, focus back to the opener on close
    useEffect(() => {
        opener.current = document.activeElement
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        inputRef.current?.focus()
        return () => {
            document.body.style.overflow = prevOverflow
            if (opener.current instanceof HTMLElement) opener.current.focus()
        }
    }, [])

    useEffect(() => {
        setActive(0)
        setShowRelated(false)
    }, [query])
    useEffect(() => {
        if (active >= options.length) setActive(options.length ? options.length - 1 : 0)
    }, [active, options.length])
    useEffect(() => {
        if (!current) return
        document.getElementById(optionId(current.id))?.scrollIntoView({ block: 'nearest' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current?.id])

    const go = (path: string) => {
        // "/staff/erp/doadores?busca=…" -> caminho + busca, para o router
        const { to, search } = splitPath(path)
        onClose()
        void navigate({ to: to as '/', search: search as never })
    }
    const choose = (opt: PaletteOption) => go(optionPath(opt))

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            if (showRelated && !wide) setShowRelated(false)
            else onClose()
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault()
            setShowRelated(false)
            setActive(i => Math.max(0, wrapIndex(i, e.key === 'ArrowDown' ? 1 : -1, options.length)))
        } else if (e.key === 'ArrowRight' && !wide && ref && caretAtEnd(inputRef.current)) {
            e.preventDefault()
            setShowRelated(true)
        } else if (e.key === 'ArrowLeft' && !wide && showRelated) {
            e.preventDefault()
            setShowRelated(false)
        } else if (e.key === 'Enter' && current) {
            e.preventDefault()
            choose(current)
        } else if (e.key === 'Tab') {
            // Only the input is focusable inside; keep focus here
            e.preventDefault()
            inputRef.current?.focus()
        }
    }

    const typed = query.trim()
    const searching = shouldSearch(typed) && !adminMe.isError
    const waiting = searching && (debounced !== typed || search.isFetching) && !groups
    const hasGroups = sections.some(s => s.key !== 'telas')

    const renderOption = (opt: PaletteOption, index: number) => {
        const selected = index === active
        const common = {
            id: optionId(opt.id),
            role: 'option' as const,
            'aria-selected': selected,
            onMouseMove: () => { if (!selected) setActive(index) },
            onMouseDown: (e: React.MouseEvent) => e.preventDefault(),
            onClick: () => choose(opt),
            className: cn('flex min-h-9 cursor-pointer items-center gap-2.5 rounded-[10px] px-3 py-1.5', selected && 'bg-[var(--hover)]'),
        }
        if (opt.kind === 'page') {
            const p = opt.page
            return (
                <div key={opt.id} {...common}>
                    <ArrowRight size={14} strokeWidth={1.8} aria-hidden="true" className="flex-none text-muted" />
                    <span className="min-w-0 flex-1 truncate text-[13.5px] text-fg">
                        {p.label}
                        {p.title && <span className="text-muted"> · {p.title}</span>}
                    </span>
                    <span className="flex-none text-[12px] text-muted">{p.group}</span>
                    {selected && <CornerDownLeft size={13} aria-hidden="true" className="flex-none text-muted" />}
                </div>
            )
        }
        if (opt.kind === 'more') {
            return (
                <div key={opt.id} {...common}>
                    <span className="min-w-0 flex-1 truncate pl-[2px] text-[12.5px] font-medium text-green">{opt.label} ›</span>
                    {selected && <CornerDownLeft size={13} aria-hidden="true" className="flex-none text-muted" />}
                </div>
            )
        }
        const r = opt.item
        return (
            <div key={opt.id} {...common}>
                {r.badge && <span className="flex-none rounded-full border border-line px-2 text-[11px] leading-[19px] text-muted">{r.badge}</span>}
                <span className="flex min-w-0 flex-1 flex-col leading-[1.3]">
                    <span className="truncate text-[13.5px] font-medium text-fg">{r.label}</span>
                    {r.sub && <span className="truncate text-[12px] text-muted">{r.sub}</span>}
                </span>
                {selected && r.related && !wide && (
                    <button
                        type="button"
                        tabIndex={-1}
                        onMouseDown={e => e.preventDefault()}
                        onClick={e => { e.stopPropagation(); setShowRelated(true) }}
                        className="flex flex-none items-center gap-0.5 rounded-[8px] border border-line px-1.5 text-[11.5px] leading-[20px] text-muted"
                    >
                        Relacionados<ChevronRight size={12} aria-hidden="true" />
                    </button>
                )}
                {selected && (wide || !r.related) && <CornerDownLeft size={13} aria-hidden="true" className="flex-none text-muted" />}
            </div>
        )
    }

    let index = 0
    const list = (
        <div id={`${baseId}-list`} role="listbox" aria-label="Resultados" className={cn('min-h-0 flex-1 overflow-y-auto px-1.5 pb-2', !wide && showRelated && 'hidden')}>
            {!typed && <p className="m-0 px-3 pt-3 text-[12px] text-muted">Digite um nome, código, cidade ou número da ocorrência</p>}
            {sections.map(section => (
                <div key={section.key} role="group" aria-label={section.label}>
                    <div className={sectionLabel} aria-hidden="true">{section.label}</div>
                    {section.note && <p className="m-0 px-3 pb-1 text-[12px] text-muted" role="status">{section.note}</p>}
                    {section.options.map(o => renderOption(o, index++))}
                </div>
            ))}
            {searching && !hasGroups && (
                <p className="m-0 px-3 py-3 text-[13px] text-muted" role="status">
                    {waiting ? 'Buscando…' : search.isError ? 'A busca não respondeu agora' : 'Nada encontrado'}
                </p>
            )}
            {!searching && typed && !sections.length && <p className="m-0 px-3 py-3 text-[13px] text-muted" role="status">Nada encontrado</p>}
        </div>
    )

    const preview = previewOn && (
        <RelatedPane
            item={current?.kind === 'item' ? current.item : null}
            waiting={!!ref && (!settledRef || related.isFetching) && !related.data}
            data={settledRef && related.data && related.data.id.toLowerCase() === settledRef.id.toLowerCase() ? related.data : null}
            failed={related.isError}
            onBack={wide ? null : () => setShowRelated(false)}
            onGo={go}
        />
    )

    return createPortal(
        <div className="fixed inset-0 z-50 flex justify-center px-4" style={{ paddingTop: '12vh' }} onKeyDown={onKeyDown}>
            <div aria-hidden="true" className="absolute inset-0 bg-[rgba(35,35,31,.28)]" onClick={onClose} />
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Buscar no painel"
                className={cn('relative flex h-fit max-h-[74vh] w-full flex-col overflow-hidden rounded-[14px] border border-line bg-panel', wide ? 'max-w-[880px]' : 'max-w-[560px]')}
                style={{ boxShadow: '0 12px 32px var(--shadow), 0 2px 6px var(--shadow)' }}
            >
                <div className="flex items-center gap-2.5 border-b border-line px-4">
                    <Search size={17} strokeWidth={1.8} aria-hidden="true" className="flex-none text-muted" />
                    <input
                        ref={inputRef}
                        type="text"
                        role="combobox"
                        aria-expanded="true"
                        aria-controls={`${baseId}-list`}
                        aria-activedescendant={current && !(showRelated && !wide) ? optionId(current.id) : undefined}
                        aria-autocomplete="list"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder="Buscar padrinhos, crianças, igrejas, ocorrências, telas…"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        className="h-12 min-w-0 flex-1 border-0 bg-transparent text-[14.5px] text-fg outline-none placeholder:text-muted"
                    />
                    <kbd className="flex-none rounded-[6px] border border-line px-1.5 font-sans text-[11.5px] leading-[18px] text-muted">Esc</kbd>
                </div>
                <div className="flex min-h-0 flex-1">
                    {list}
                    {wide ? <div className="w-[320px] flex-none overflow-y-auto border-l border-line">{preview}</div> : showRelated && <div className="min-h-0 flex-1 overflow-y-auto">{preview}</div>}
                </div>
            </div>
        </div>,
        document.body,
    )
}

/** Caret at the end of the input: → opens the preview without stealing the cursor movement while editing. */
function caretAtEnd(input: HTMLInputElement | null): boolean {
    return !input || input.selectionStart === input.value.length
}

function RelatedPane({ item, waiting, data, failed, onBack, onGo }: {
    item: { label: string; sub?: string; related: RelatedRef | null } | null
    waiting: boolean
    data: Related | null
    failed: boolean
    onBack: (() => void) | null
    onGo: (path: string) => void
}) {
    return (
        <section aria-label="Relacionados" aria-live="polite" className="px-4 py-3">
            {onBack && (
                <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={onBack} className="mb-2 flex items-center gap-1 text-[12px] text-muted">
                    <ArrowLeft size={13} aria-hidden="true" />Voltar aos resultados
                </button>
            )}
            {!item ? (
                <p className="m-0 text-[12.5px] text-muted">Passe por um padrinho, criança ou ficha para ver o que está ligado.</p>
            ) : (
                <>
                    <h2 className="m-0 truncate text-[13.5px] font-semibold text-fg">{item.label}</h2>
                    {item.sub && <p className="m-0 truncate text-[12px] text-muted">{item.sub}</p>}
                    {!item.related && <p className="mt-3 mb-0 text-[12.5px] text-muted">Sem prévia para este item. Enter abre a tela.</p>}
                    {item.related && waiting && <p className="mt-3 mb-0 text-[12.5px] text-muted" role="status">Carregando o que está ligado…</p>}
                    {item.related && !waiting && failed && !data && <p className="mt-3 mb-0 text-[12.5px] text-muted" role="status">Não deu para carregar agora.</p>}
                    {item.related && data && (
                        <>
                            {data.summary && <p className="mt-2 mb-0 text-[13px] leading-[1.4] text-fg">{data.summary}</p>}
                            <ul className="m-0 mt-2.5 flex list-none flex-col gap-0.5 p-0">
                                {data.rows.map(r => (
                                    <li key={r.key}>
                                        <button
                                            type="button"
                                            tabIndex={-1}
                                            disabled={!r.path}
                                            onMouseDown={e => e.preventDefault()}
                                            onClick={() => r.path && onGo(r.path)}
                                            className={cn('flex w-full items-baseline gap-2 rounded-[8px] px-1.5 py-1 text-left', r.path && 'cursor-pointer hover:bg-[var(--hover)]')}
                                        >
                                            <span className="w-[92px] flex-none truncate text-[12px] text-muted">{r.label}</span>
                                            <span className="flex min-w-0 flex-1 flex-col leading-[1.35]">
                                                <span className="text-[13px]" style={{ color: TONE_COLOR[r.tone] ?? 'var(--text)' }}>{r.value}</span>
                                                {r.detail && <span className="text-[12px] text-muted">{r.detail}</span>}
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            {!data.rows.length && !data.notes.length && <p className="mt-2 mb-0 text-[12.5px] text-muted">Nada ligado por enquanto.</p>}
                            {data.notes.map(n => <p key={n} className="mt-2 mb-0 text-[12px] text-muted">{n}</p>)}
                            {data.ficha && (
                                <button type="button" tabIndex={-1} onMouseDown={e => e.preventDefault()} onClick={() => onGo(data.ficha!)} className="mt-3 text-[12.5px] font-semibold text-green">
                                    Abrir a ficha ›
                                </button>
                            )}
                        </>
                    )}
                </>
            )}
        </section>
    )
}
